import React, { useState, useEffect, useCallback, useContext } from 'react';
import AuthContext from '../context/AuthContext';
import Modal from '../components/Modal';
import JadwalServisForm from '../components/JadwalServisForm';
import Pagination from '../components/Pagination';
import api from '../api/axiosConfig';
import { showSuccessToast, showErrorAlert, showConfirmDialog } from '../utils/alert';
import './ServicePage.css';

const JadwalServisPage = () => {
  const { user } = useContext(AuthContext);
  const [services, setServices] = useState([]);
  const [isModalOpen, setModalOpen] = useState(false);
  const [currentData, setCurrentData] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState('');

  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);
  const [totalItems, setTotalItems] = useState(0);
  const [totalPages, setTotalPages] = useState(0);

  const API_URL = '/api/jadwal-servis';

  const fetchServices = useCallback(async (page = 1, limit = itemsPerPage, keyword = debouncedSearchTerm) => {
    try {
      const response = await api.get(`${API_URL}?keyword=${keyword}&page=${page}&limit=${limit}`);
      setServices(response.data.data);
      setTotalItems(response.data.totalItems);
      setTotalPages(response.data.totalPages);
      setCurrentPage(response.data.currentPage);
    } catch (error) {
      console.error("Gagal mengambil data jadwal servis:", error);
      showErrorAlert("Gagal memuat data jadwal servis.");
    }
  }, [itemsPerPage, debouncedSearchTerm]);

  useEffect(() => {
    const timerId = setTimeout(() => {
      setDebouncedSearchTerm(searchTerm);
      setCurrentPage(1);
    }, 500);
    return () => clearTimeout(timerId);
  }, [searchTerm]);

  useEffect(() => {
    fetchServices(currentPage, itemsPerPage, debouncedSearchTerm);
  }, [debouncedSearchTerm, currentPage, itemsPerPage, fetchServices]);

  const handlePageChange = (pageNumber) => {
    fetchServices(pageNumber);
  };

  const handleItemsPerPageChange = (limit) => {
    setItemsPerPage(limit);
    fetchServices(1, limit);
  };

  const handleSave = async (formData) => {
    const isEditing = !!currentData;

    if (!isEditing && user.role === 'user') {
        showErrorAlert('Anda tidak memiliki izin untuk mengedit data.');
        return;
    }

    const url = isEditing ? `${API_URL}/${currentData._id}` : API_URL;
    const method = isEditing ? 'put' : 'post';

    try {
      await api[method](url, formData);
      setModalOpen(false);
      fetchServices(currentPage);
      showSuccessToast(isEditing ? 'Jadwal berhasil diperbarui!' : 'Jadwal berhasil ditambahkan!');
    } catch (error) {
      console.error("Error saat menyimpan:", error);
      showErrorAlert('Gagal menyimpan data. Silakan coba lagi.');
    }
  };

  const handleDelete = async (id) => {
    if (user.role === 'user' || user.role === 'operator') {
       showErrorAlert('Anda tidak memiliki izin untuk menghapus data.');
       return;
    }
    const result = await showConfirmDialog();
    if (result.isConfirmed) {
      try {
        await api.delete(`${API_URL}/${id}`);
        fetchServices(currentPage);
        showSuccessToast('Jadwal berhasil dihapus!');
      } catch (error) {
        console.error("Gagal menghapus data:", error);
        showErrorAlert('Gagal menghapus data. Silakan coba lagi.');
      }
    }
  };

  const openModalForEdit = (service) => {
    setCurrentData(service);
    setModalOpen(true);
  };
  
  const openModalForAdd = () => {
    setCurrentData(null);
    setModalOpen(true);
  };

  return (
    <div className="page-container">
      <div className="page-header">
        <p>Manage Service / Jadwal Service</p>
        <h1>Data Jadwal Service Kendaraan</h1>
      </div>

      <div className="content-card">
        <div className="toolbar">
          {(user.role === 'superadmin' || user.role === 'operator') && (
            <button className="btn-primary" onClick={openModalForAdd}>+ Tambah Data</button>
          )}
          <input 
            type="text"
            placeholder="Cari No. Kendaraan, Odometer..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            style={{ marginLeft: 'auto' }}
          />
        </div>

        <div className="table-responsive-wrapper">
          <table className="data-table">
            <thead>
              <tr>
                <th>No.</th>
                <th>Nomor Kendaraan</th>
                <th>Odometer (KM)</th>
                <th>Tanggal</th>
                <th>User</th>
                <th>Pembuat</th>
                <th>Aksi</th>
              </tr>
            </thead>
            <tbody>
              {services.map((service, index) => (
                <tr key={service._id}>
                  <td>{(currentPage - 1) * itemsPerPage + index + 1}</td>
                  <td>{service.kendaraan ? `${service.kendaraan.nomorKendaraan} - ${service.kendaraan.jenis}` : 'N/A'}</td>
                  <td>{service.odometer || '-'}</td>
                  <td>{service.tanggal ? new Date(service.tanggal).toLocaleDateString('id-ID') : '-'}</td>
                  <td>{service.assignedUser?.name || '-'}</td>
                  <td>{service.createdBy?.name || 'N/A'}</td>
                  <td className="action-buttons">
                    {(user.role !== 'user' || service.assignedUser?._id === user._id) && (
                      <button onClick={() => openModalForEdit(service)} title="Edit">✏️</button>
                    )}
                    {(user.role === 'superadmin') && (
                      <button onClick={() => handleDelete(service._id)} title="Hapus">🗑️</button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        <Pagination
          currentPage={currentPage}
          totalPages={totalPages}
          itemsPerPage={itemsPerPage}
          totalItems={totalItems}
          onPageChange={handlePageChange}
          onItemsPerPageChange={handleItemsPerPageChange}
        />

      </div>

      {isModalOpen && (
        <Modal show={isModalOpen} onClose={() => setModalOpen(false)} title={currentData ? "Edit Jadwal Service" : "Tambah Jadwal Service"}>
          <JadwalServisForm
            existingData={currentData}
            onSave={handleSave}
            onCancel={() => setModalOpen(false)}
          />
        </Modal>
      )}
    </div>
  );
};

export default JadwalServisPage;